import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import notify from "../../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Calendar, Clock3 } from "lucide-react";
import { estimatedChargesApi } from "../../Utils/estimatedChargesApi";

const AddActualsModal = ({
                             onClose,
                             shipmentDetails,
                             shipmentNumber,
                             fetchShipmentDetails,
                             mode, // "release" | "pickup" | "dropoff"
                             isShipper,
                             isCarrier,
                             paymentType,
                             availableStops = [], // Array of stops that user can add actuals for
                         }) => {
    console.log("Order number:", shipmentDetails.salesOrderNumber);
    console.log("And details:", shipmentDetails);

    const putOrderStatus = async ({ orderNumber, officeCode, status, token }) => {
        return axios.put(
            `${process.env.REACT_APP_API_BASE_URL}/api/orders/order-number/${orderNumber}/update`,
            { status },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                    "Office-Code": officeCode,
                },
            }
        );
    };

    function normalizeOrderNumbers(salesOrderNumber) {
        if (typeof salesOrderNumber !== "string") return [];
        return salesOrderNumber
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    }

    const formatMDYHM = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        const date = d.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        });
        const time = d.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
        return `${date} ${time}`;
    };

    const [actualGateIn, setActualGateIn] = useState("");
    const [actualGateOut, setActualGateOut] = useState("");
    const [releaseNumber, setReleaseNumber] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedStopId, setSelectedStopId] = useState("");
    const [gateInDate, setGateInDate] = useState(null);
    const [gateOutDate, setGateOutDate] = useState(null);
    const [gateInTime, setGateInTime] = useState("");
    const [gateOutTime, setGateOutTime] = useState("");
    const [showGateInPicker, setShowGateInPicker] = useState(false);
    const [showGateOutPicker, setShowGateOutPicker] = useState(false);
    const [showStopDropdown, setShowStopDropdown] = useState(false);

    const showRelease = mode === "release";
    const showActuals = mode === "pickup" || mode === "dropoff";

    const title =
        mode === "release"
            ? "Add Release Number"
            : mode === "pickup"
                ? "Add Actual Pickup"
                : mode === "dropoff"
                    ? "Add Actual Dropoff"
                    : "";

    const normalizeDay = (value) => {
        if (!value) return null;
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const buildDateTimeValue = (dateObj, timeStr) => {
        if (!dateObj || !timeStr) return "";
        const parts = timeStr.split(":").map(Number);
        const hh = parts[0] || 0;
        const mm = parts[1] || 0;
        const ss = parts[2] || 0;
        const dt = new Date(dateObj);
        dt.setHours(hh, mm, ss, 0);
        const y = dt.getFullYear();
        const mo = String(dt.getMonth() + 1).padStart(2, "0");
        const d = String(dt.getDate()).padStart(2, "0");
        const h = String(dt.getHours()).padStart(2, "0");
        const min = String(dt.getMinutes()).padStart(2, "0");
        const sec = String(dt.getSeconds()).padStart(2, "0");
        return `${y}-${mo}-${d}T${h}:${min}:${sec}`;
    };

    const isTimeAllowed = (dateObj, timeStr, { min, max }) => {
        if (!dateObj || !timeStr) return false;
        const candidate = new Date(buildDateTimeValue(dateObj, timeStr));
        if (min && candidate < min) return false;
        if (max && candidate > max) return false;
        return true;
    };

    const TimePicker = ({
        value,
        onChange,
        selectedDate,
        minDateTime,
        maxDateTime,
    }) => {
        const [open, setOpen] = useState(false);
        const [hour, setHour] = useState("");
        const [minute, setMinute] = useState("");
        const [second, setSecond] = useState("00");
        const [meridiem, setMeridiem] = useState("AM");
        const dropdownRef = useRef(null);

        useEffect(() => {
            const handler = (e) => {
                if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                    setOpen(false);
                }
                if (e.key === "Escape") setOpen(false);
            };
            document.addEventListener("mousedown", handler);
            document.addEventListener("keydown", handler);
            return () => {
                document.removeEventListener("mousedown", handler);
                document.removeEventListener("keydown", handler);
            };
        }, []);

        // Parse existing value (HH:MM or HH:MM:SS) into fields when dropdown opens
        useEffect(() => {
            if (!open) return;
            if (!value) {
                setHour("");
                setMinute("");
                setSecond("");
                setMeridiem("AM");
                return;
            }
            const parts = value.split(":");
            const hh = parts[0] || "0";
            const mm = parts[1] || "00";
            const ss = parts[2] || "00";
            const hours = Number(hh);
            const mins = Number(mm);
            const secs = Number(ss);
            if (Number.isNaN(hours) || Number.isNaN(mins)) return;
            const hour12 = hours % 12 === 0 ? 12 : hours % 12;
            setHour(String(hour12).padStart(2, "0"));
            setMinute(String(Math.max(0, Math.min(59, mins))).padStart(2, "0"));
            setSecond(String(Math.max(0, Math.min(59, secs))).padStart(2, "0"));
            setMeridiem(hours >= 12 ? "PM" : "AM");
        }, [open, value]);

        // Display helper: formats a stored "HH:MM:SS" or "HH:MM" value as "H:MM:SS AM/PM"
        const format12Hour = (val) => {
            if (!val) return "";
            const parts = val.split(":");
            const hh = parts[0] || "0";
            const mm = parts[1] || "00";
            const ss = parts[2] || "00";
            const hours = Number(hh);
            if (Number.isNaN(hours)) return val;
            const hour12 = hours % 12 === 0 ? 12 : hours % 12;
            const ampm = hours < 12 ? "AM" : "PM";
            return `${hour12}:${mm}:${ss} ${ampm}`;
        };

        // Builds a "HH:MM:SS" 24-hour string from current picker state
        const to24HourString = () => {
            if (hour === "" || minute === "") return "";
            const hNum = Math.max(1, Math.min(12, parseInt(hour, 10) || 12));
            const mNum = Math.max(0, Math.min(59, parseInt(minute, 10) || 0));
            const sNum = Math.max(0, Math.min(59, parseInt(second, 10) || 0));
            const hour24 = meridiem === "PM" ? (hNum % 12) + 12 : hNum % 12;
            return `${String(hour24).padStart(2, "0")}:${String(mNum).padStart(2, "0")}:${String(sNum).padStart(2, "0")}`;
        };

        const applyTime = () => {
            const candidate = to24HourString();
            if (
                selectedDate &&
                !isTimeAllowed(selectedDate, candidate, { min: minDateTime, max: maxDateTime })
            ) {
                return;
            }
            if (!candidate) return;
            onChange(candidate);
            setOpen(false);
        };

        return (
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => setOpen((p) => !p)}
                    className="w-[150px] border border-gray-300 dark:border-acs-dark-border rounded px-fluid-input-x py-fluid-input-y text-fluid-base bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text flex items-center justify-between"
                >
                    <span className="truncate">{value ? format12Hour(value) : "Select time"}</span>
                    <Clock3 size={16} className="ml-1 shrink-0 text-gray-500" />
                </button>
                {open && (
                    <div className="absolute z-50 mt-2 w-64 bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-lg shadow-lg p-3">
                        {/* HH : MM : SS row */}
                        <div className="flex items-center gap-1 mb-2">
                            {/* Hour */}
                            <input
                                type="text"
                                inputMode="numeric"
                                value={hour}
                                onChange={(e) => {
                                    const val = (e.target.value || "").replace(/\D/g, "").slice(0, 2);
                                    setHour(val);
                                }}
                                className="w-12 border border-gray-300 dark:border-acs-dark-border rounded px-2 py-1 text-center text-sm bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text"
                                placeholder="HH"
                            />
                            <span className="text-sm text-gray-600 dark:text-acs-dark-subtext">:</span>
                            {/* Minute */}
                            <input
                                type="text"
                                inputMode="numeric"
                                value={minute}
                                onChange={(e) => {
                                    const val = (e.target.value || "").replace(/\D/g, "").slice(0, 2);
                                    setMinute(val);
                                }}
                                className="w-12 border border-gray-300 dark:border-acs-dark-border rounded px-2 py-1 text-center text-sm bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text"
                                placeholder="MM"
                            />
                            <span className="text-sm text-gray-600 dark:text-acs-dark-subtext">:</span>
                            {/* Second */}
                            <input
                                type="text"
                                inputMode="numeric"
                                value={second}
                                onChange={(e) => {
                                    const val = (e.target.value || "").replace(/\D/g, "").slice(0, 2);
                                    setSecond(val);
                                }}
                                className="w-12 border border-gray-300 dark:border-acs-dark-border rounded px-2 py-1 text-center text-sm bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text"
                                placeholder="SS"
                            />
                            {/* AM / PM */}
                            <div className="flex flex-col gap-1 ml-1">
                                {["AM", "PM"].map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => setMeridiem(m)}
                                        className={`px-2 py-1 text-xs rounded border ${
                                            meridiem === m
                                                ? "bg-blue-600 text-white border-blue-600"
                                                : "border-gray-300 text-gray-700 dark:text-acs-dark-text"
                                        }`}
                                    >
                                        {m}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* Field labels */}
                        <div className="flex items-center gap-1 mb-2 px-0.5">
                            <span className="w-12 text-center text-[10px] text-gray-400">Hour</span>
                            <span className="w-2" />
                            <span className="w-12 text-center text-[10px] text-gray-400">Min</span>
                            <span className="w-2" />
                            <span className="w-12 text-center text-[10px] text-gray-400">Sec</span>
                        </div>
                        <button
                            type="button"
                            onClick={applyTime}
                            className="w-full mt-1 bg-blue-600 text-white text-sm font-semibold px-3 py-2 rounded hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={
                                selectedDate &&
                                !isTimeAllowed(selectedDate, to24HourString(), { min: minDateTime, max: maxDateTime })
                            }
                        >
                            Set time
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const FUTURE_LIMIT_MS = 3 * 60 * 60 * 1000;
    const futureLimitDate = new Date(Date.now() + FUTURE_LIMIT_MS);

    const handleConfirm = async () => {
        if (showRelease && !releaseNumber) {
            notify.error({
                title: "Missing Release Number",
                message: "Please enter a release number.",
            });
            return;
        }
        if (showActuals && !selectedStopId) {
            notify.error({
                title: "Missing Stop Selection",
                message: "Please select a stop to add actuals for.",
            });
            return;
        }
        if (showActuals && (!actualGateIn || !actualGateOut)) {
            notify.error({
                title: "Missing Actuals",
                message: "Please fill Actual Gate-In and Gate-Out.",
            });
            return;
        }

        const gateInTime = actualGateIn ? new Date(actualGateIn).getTime() : null;
        const gateOutTime = actualGateOut ? new Date(actualGateOut).getTime() : null;

        const nowMs = Date.now();
        const futureLimitMs = nowMs + FUTURE_LIMIT_MS;
        if (
            showActuals &&
            ((gateInTime && gateInTime > futureLimitMs) ||
                (gateOutTime && gateOutTime > futureLimitMs))
        ) {
            notify.error({
                title: "Invalid Actuals",
                message: "Actual times invalid.",
            });
            return;
        }

        if (gateInTime && gateOutTime && gateOutTime < gateInTime) {
            notify.error({
                title: "Invalid Actuals",
                message: "Actual Gate-Out cannot be earlier than Actual Gate-In.",
            });
            return;
        }

        if (mode === "dropoff") {
            const pickupStop = shipmentDetails?.shipmentStops?.find(
                (s) => s.stopType === "PICKUP"
            );

            if (pickupStop?.actualGateOut) {
                const pickupOut = new Date(pickupStop.actualGateOut).getTime();

                if (
                    (gateInTime && gateInTime < pickupOut) ||
                    (gateOutTime && gateOutTime < pickupOut)
                ) {
                    notify.error({
                        title: "Invalid Dropoff Actuals",
                        message:
                            "Dropoff times cannot be earlier than the Pickup Actual Gate-Out.",
                    });
                    setIsSubmitting(false);
                    return;
                }
            }
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        const freshToken = Cookies.get("authToken");
        const freshOfficeCode =
            shipmentDetails?.officeCode || Cookies.get("officeCode");

        try {
            let payload = {};
            let nextStatus = shipmentDetails?.status;

            if (mode === "release") {
                payload.releaseNumber = releaseNumber;
                nextStatus = "Ready for Pickup";
                payload.status = nextStatus;
            }

            let stopHasAppointment = false;
            let isCollect = false;

            let actualStop = null;
            if (showActuals) {
                console.log("availableStops", availableStops);
                console.log("selectedStopId", selectedStopId);

                actualStop = availableStops.find((s) => s.id === selectedStopId);

                if (!actualStop) {
                    notify.error({
                        title: "Stop Not Found",
                        message: "Selected stop not found.",
                    });
                    setIsSubmitting(false);
                    return;
                }

                if (mode === "dropoff") {
                    const dropStops =
                        (shipmentDetails?.shipmentStops || []).filter(
                            (s) => (s.stopType || "").toUpperCase() === "DROP_OFF"
                        ) || [];
                    const currentSeq = actualStop.stopSequence ?? 0;
                    const maxSeq = Math.max(
                        ...dropStops.map((s) => s.stopSequence ?? 0),
                        0
                    );
                    if (currentSeq >= maxSeq) {
                        const missingPrior = dropStops.filter(
                            (s) =>
                                (s.stopSequence ?? 0) < currentSeq &&
                                !s.actualGateOut
                        );
                        if (missingPrior.length > 0) {
                            notify.error({
                                title: "Add earlier drop-off actuals first",
                                message:
                                    "Please enter actuals for the previous drop-off stops before completing the final drop-off.",
                            });
                            setIsSubmitting(false);
                            return;
                        }
                    }
                }

                stopHasAppointment = !!(actualStop?.appointmentStart);
                isCollect = (paymentType || "").toLowerCase() === "collect";

                payload.shipmentStops = [
                    {
                        id: actualStop.id,
                        stopNumber: actualStop.stopNumber,
                        actualGateIn,
                        actualGateOut,
                        ...(actualStop.readyForPickupDateTime
                            ? { readyForPickupDateTime: actualStop.readyForPickupDateTime }
                            : {}),
                    },
                ];

                if (mode === "pickup") {
                    if (paymentType?.toLowerCase() === "collect") {
                        nextStatus = "Completed";
                        payload.status = nextStatus;
                    } else {
                        nextStatus = "Active";
                        payload.status = nextStatus;
                    }
                } else if (mode === "dropoff") {
                    const maxStopSequence = Math.max(
                        ...availableStops.map((stop) => stop.stopSequence || 0)
                    );
                    const isHighestSequence =
                        (actualStop.stopSequence || 0) >= maxStopSequence;

                    console.log("Selected stop sequence:", actualStop.stopSequence);
                    console.log("Max stop sequence in available stops:", maxStopSequence);
                    console.log("Is highest sequence:", isHighestSequence);

                    if (isHighestSequence) {
                        nextStatus = "Completed";
                        payload.status = nextStatus;
                    }
                }
            }


            const res = await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/api/shipments/${shipmentNumber}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${freshToken}`,
                        "Office-Code": freshOfficeCode,
                    },
                }
            );

            const orderNumbers = normalizeOrderNumbers(
                shipmentDetails?.salesOrderNumber
            );

            if (nextStatus === "Completed" && orderNumbers.length > 0) {
                await Promise.allSettled(
                    orderNumbers.map((orderNumber) =>
                        putOrderStatus({
                            orderNumber,
                            officeCode: freshOfficeCode,
                            status: "Completed",
                            token: freshToken,
                        })
                    )
                );

                notify.success({
                    title: "Order(s) Updated",
                    message: `Order status for ${orderNumbers.join(", ")} updated to Completed.`,
                });
            }

            if (res.status === 200) {
                console.log("appointment", stopHasAppointment);

                if (showActuals && actualStop && !isCollect && stopHasAppointment) {
                    try {
                        const response = await estimatedChargesApi.calculateStopEstimatedCharges({
                            token: freshToken,
                            officeCode: freshOfficeCode,
                            shipmentNumber,
                            stopId: selectedStopId,
                            stopType: actualStop.stopType,
                            actualGateIn,
                            actualGateOut,
                            appointmentStartTime: actualStop.appointmentStart || null,
                            appointmentEndTime: actualStop.appointmentEnd || null,
                        });

                        if (response.status === 200 && response.data) {
                            console.log("Estimated charges calculated successfully:", response.data);
                        } else if (response.status === 204) {
                            console.log("No estimated charges found for this shipment");
                        }
                    } catch (err) {
                        console.error("Failed to calculate estimated charges:", err);
                    }
                }

                const fmt = formatMDYHM;

                if (mode === "release") {
                    notify.success({
                        title: "Release Number Added",
                        message: `#${shipmentNumber} — ${releaseNumber}`,
                    });
                } else {
                    const phase = mode === "pickup" ? "Pickup" : "Dropoff";
                    notify.success({
                        title: "Actuals Saved",
                        message: `#${shipmentNumber} — ${phase} | In: ${fmt(actualGateIn)} | Out: ${fmt(actualGateOut)} | Status: ${nextStatus}`,
                    });
                }
                await fetchShipmentDetails();
                onClose();
            } else {
                notify.error({ title: "Save Failed", message: "Please try again." });
            }
        } catch (e) {
            console.error("Save error:", e);
            notify.error({
                title: "Save Failed",
                message:
                    e?.response?.data?.message ||
                    e?.message ||
                    "Unexpected error occurred.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    let pickupStop = null;
    let pickupMinTime = null;

    if (mode === "dropoff") {
        pickupStop = shipmentDetails?.shipmentStops?.find(
            (s) => s.stopType === "PICKUP"
        );

        if (pickupStop?.actualGateOut) {
            const d = new Date(pickupStop.actualGateOut);
            const offset = d.getTimezoneOffset();
            const local = new Date(d.getTime() - offset * 60 * 1000);
            // Keep seconds in the min time string
            pickupMinTime = local.toISOString().slice(0, 19);
        }
    }

    const today = normalizeDay(new Date());
    const pickupMinDateObj = normalizeDay(mode === "dropoff" ? pickupMinTime : null);

    return (
        <div className="
            fixed inset-0 z-50 flex items-center justify-center
            bg-custom-bg-purple bg-opacity-50 dark:bg-black/70
            px-4
            ">
            <div className="
                    bg-white dark:bg-acs-dark-surface
                    rounded-xl shadow w-full max-w-md p-fluid-3 space-y-fluid-3
                    border border-gray-200 dark:border-acs-dark-border
                ">
                <div className="
                    flex justify-between items-center
                    border-b border-gray-300 dark:border-acs-dark-border
                    pb-fluid-2
                    ">
                    <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 dark:text-acs-dark-subtext hover:text-gray-900 dark:hover:text-white text-fluid-xl transition-colors"
                    >
                        ✕
                    </button>
                </div>

                <div className="space-y-fluid-2">
                    {showRelease && (
                        <div>
                            <label className="block text-fluid-sm font-medium text-gray-700">
                                Release Number
                            </label>
                            <input
                                type="text"
                                value={releaseNumber}
                                onChange={(e) => setReleaseNumber(e.target.value)}
                                placeholder="Enter release number"
                                className="w-full border border-gray-300 rounded px-fluid-input-x py-fluid-input-y text-fluid-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    )}

                    {showActuals && (
                        <>
                            <div className="text-xs text-gray-500 dark:text-acs-dark-subtext">
                                Time inputs use 12-hour clock (AM/PM) with seconds.
                            </div>

                            <div>
                                <label className="block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
                                    Select Stop
                                </label>
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowStopDropdown((prev) => !prev)}
                                        className="w-full border border-gray-300 dark:border-acs-dark-border rounded px-fluid-input-x py-fluid-input-y text-fluid-base bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-[#3B5CCC]"
                                    >
                                        <span>
                                            {(() => {
                                                const current = availableStops.find((s) => String(s.id) === String(selectedStopId));
                                                if (!current) return "Select a stop";
                                                const label = current.stopName || "Unnamed";
                                                const type = current.stopType === "PICKUP" ? "Pickup" : "Dropoff";
                                                return `${label} (${type})`;
                                            })()}
                                        </span>
                                        <svg
                                            className={`ml-2 w-4 h-4 transform transition-transform duration-200 ${showStopDropdown ? "rotate-180" : ""}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {showStopDropdown && (
                                        <div className="absolute left-0 right-0 mt-fluid-1 max-h-60 overflow-y-auto bg-white dark:bg-acs-dark-surface border border-gray-300 dark:border-acs-dark-border rounded shadow-lg z-10 animate-in fade-in-0 zoom-in-95">
                                            {availableStops.map((stop) => {
                                                const label = stop.stopName || "Unnamed";
                                                const type = stop.stopType === "PICKUP" ? "Pickup" : "Dropoff";
                                                const note = stop.appointmentStart
                                                    ? `Window: ${formatMDYHM(stop.appointmentStart)}`
                                                    : "";
                                                return (
                                                    <button
                                                        key={stop.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedStopId(stop.id);
                                                            setShowStopDropdown(false);
                                                        }}
                                                        className="w-full text-left px-fluid-2 py-fluid-1.5 hover:bg-gray-100 dark:hover:bg-[#1f222a] text-fluid-sm"
                                                    >
                                                        <div className="font-semibold text-gray-900 dark:text-acs-dark-text">
                                                            {label} ({type})
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-acs-dark-subtext">
                                                            Stop #{stop.stopNumber ?? "—"} {note ? `• ${note}` : ""}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {mode === "dropoff" && (
                                <div className="rounded-md border border-gray-200 dark:border-acs-dark-border bg-gray-50 dark:bg-acs-dark-hover px-3 py-2 text-sm text-gray-700 dark:text-acs-dark-text">
                                    <div className="font-semibold mb-1">Pickup Actuals</div>
                                    <div className="flex flex-col gap-1">
                                        <span>
                                            Gate-In: {pickupStop?.actualGateIn ? formatMDYHM(pickupStop.actualGateIn) : "—"}
                                        </span>
                                        <span>
                                            Gate-Out: {pickupStop?.actualGateOut ? formatMDYHM(pickupStop.actualGateOut) : "—"}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* 🕒 Actual Gate-In */}
                            <div>
                                <label className="block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
                                    Actual Gate-In
                                </label>
                                <div className="flex items-center gap-fluid-2">
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setShowGateInPicker((prev) => !prev)}
                                            className="w-[180px] border border-gray-300 dark:border-acs-dark-border rounded px-fluid-input-x py-fluid-input-y text-fluid-base bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text flex items-center justify-between"
                                        >
                                            <span>{gateInDate ? gateInDate.toLocaleDateString("en-US") : "Select date"}</span>
                                            <Calendar size={18} className="text-gray-500" />
                                        </button>
                                        {showGateInPicker && (
                                            <div className="absolute z-50 mt-2 bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-lg shadow-lg p-2">
                                                <DayPicker
                                                    mode="single"
                                                    selected={gateInDate}
                                                    onSelect={(day) => {
                                                        setGateInDate(day);
                                                        const next = gateInTime ? buildDateTimeValue(day, gateInTime) : "";
                                                        setActualGateIn(next);
                                                        setShowGateInPicker(false);
                                                    }}
                                                    disabled={[
                                                        { after: today },
                                                        ...(mode === "dropoff" && pickupMinDateObj ? [{ before: pickupMinDateObj }] : []),
                                                    ]}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <TimePicker
                                        selectedDate={gateInDate}
                                        value={gateInTime}
                                        minDateTime={mode === "dropoff" && pickupMinTime ? new Date(pickupMinTime) : null}
                                        maxDateTime={futureLimitDate}
                                        onChange={(val) => {
                                            setGateInTime(val);
                                            setActualGateIn(gateInDate ? buildDateTimeValue(gateInDate, val) : "");
                                        }}
                                    />
                                </div>
                            </div>

                            {/* 🕒 Actual Gate-Out */}
                            <div>
                                <label className="block text-fluid-sm font-medium text-gray-700 dark:text-acs-dark-text">
                                    Actual Gate-Out
                                </label>
                                <div className="flex items-center gap-fluid-2">
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setShowGateOutPicker((prev) => !prev)}
                                            className="w-[180px] border border-gray-300 dark:border-acs-dark-border rounded px-fluid-input-x py-fluid-input-y text-fluid-base bg-white dark:bg-acs-dark-surface text-gray-900 dark:text-acs-dark-text flex items-center justify-between"
                                        >
                                            <span>{gateOutDate ? gateOutDate.toLocaleDateString("en-US") : "Select date"}</span>
                                            <Calendar size={18} className="text-gray-500" />
                                        </button>
                                        {showGateOutPicker && (
                                            <div className="absolute z-50 mt-2 bg-white dark:bg-acs-dark-surface border border-gray-200 dark:border-acs-dark-border rounded-lg shadow-lg p-2">
                                                <DayPicker
                                                    mode="single"
                                                    selected={gateOutDate}
                                                    onSelect={(day) => {
                                                        setGateOutDate(day);
                                                        const next = gateOutTime ? buildDateTimeValue(day, gateOutTime) : "";
                                                        setActualGateOut(next);
                                                        setShowGateOutPicker(false);
                                                    }}
                                                    disabled={[
                                                        { after: today },
                                                        ...(mode === "dropoff" && pickupMinDateObj ? [{ before: pickupMinDateObj }] : []),
                                                        ...(gateInDate ? [{ before: gateInDate }] : []),
                                                    ]}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <TimePicker
                                        selectedDate={gateOutDate}
                                        value={gateOutTime}
                                        minDateTime={
                                            actualGateIn
                                                ? new Date(actualGateIn)
                                                : mode === "dropoff" && pickupMinTime
                                                    ? new Date(pickupMinTime)
                                                    : null
                                        }
                                        maxDateTime={futureLimitDate}
                                        onChange={(val) => {
                                            setGateOutTime(val);
                                            setActualGateOut(gateOutDate ? buildDateTimeValue(gateOutDate, val) : "");
                                        }}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div
                    className="
                        flex justify-end gap-fluid-2 pt-fluid-2
                        border-t border-gray-200 dark:border-acs-dark-border
                    "
                >
                    <button
                        onClick={onClose}
                        className="
                            px-fluid-button-x py-fluid-button-y
                            border border-gray-300 dark:border-[#3B5CCC]
                            text-gray-700 dark:text-acs-dark-text
                            rounded transition-colors
                            hover:bg-gray-100 dark:hover:bg-acs-dark-hover
                            text-fluid-sm
                            disabled:opacity-50 disabled:cursor-not-allowed
                            "
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className={`
                            px-fluid-button-x py-fluid-button-y
                            text-white text-fluid-sm rounded font-semibold transition
                            ${
                            isSubmitting
                                ? "bg-blue-300 dark:bg-blue-900 cursor-not-allowed"
                                : "bg-blue-600 dark:bg-[#3B5CCC] hover:bg-blue-700 dark:hover:bg-[#4C6EE7]"
                        }
                        `}
                    >
                        {isSubmitting ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddActualsModal;